import { createMarkup } from '@packages/domain/utils/createMarkup/createMarkup';
import { fetchRecurringOffers } from 'actions/entities/offers/fetchOffers/fetchOffers';
import { LoadingDots } from 'components/LoadingDots/LoadingDots';
import { Autocomplete } from 'components/ui/Autocomplete/Autocomplete';
import { Button } from 'components/ui/Button/Button';
import { Spinner } from 'components/ui/Spinner';
import type { RecurringOffersMetadata } from 'models/offer';
import { useState } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import {
  getRecurringOffers,
  getRecurringOffersMetadata,
} from 'selectors/entities/offers/offers';
import { isFetching } from 'selectors/ui/ui';
import { useTranslations } from 'utils/translations/useTranslations';
import { OptionList } from '../components/OptionList/OptionList';
import { Terms } from '../components/Terms/Terms';
import { usePaymentOptions } from '../hooks/usePaymentOptions';
import { PlanSummary } from './PlanSummary';
import './RecurringPaymentOptions.scss';

type RecurringPaymentProps = ConnectedProps<typeof connector> & {
  isFetchingOffer: boolean;
  metadata?: RecurringOffersMetadata;
  onNext: (isRecurring: true) => void;
  selectOption: (name: string, value: string) => void;
};

function RecurringPaymentComponent({
  isFetchingOffer,
  isFetchingRecurring,
  metadata,
  options = [],
  fetchRecurringOffers,
  onNext,
  selectOption,
}: RecurringPaymentProps) {
  const i18n = useTranslations();
  const {
    dirtyFields,
    errors,
    handleFieldChange,
    handleOnClick,
    handleSelectOffer,
    handleTermsChange,
    isSubmitting,
    isValid,
    values,
  } = usePaymentOptions({
    onClick: () => onNext(true), // TODO here we don't create the payment yet, if the user is logged in, navigate to confirmation screen; if not, navigate to sign up
    selectOption,
  });
  const {
    numberOfInstallments = 0,
    configuredInstallments: installments = [],
  } = metadata || {};

  const canChooseInstallments = installments.length > 1;

  const [selectedInstallment, setSelectedInstallment] = useState(
    `${numberOfInstallments}`,
  );

  const installmentOptions = installments.map(installment => ({
    label: `${installment}`,
    value: `${installment}`,
  }));

  const _options = options.map(option => ({
    ...option,
    interval: Number(selectedInstallment),
  }));

  const onInstallmentChange = (value: string) => {
    setSelectedInstallment(value);
    fetchRecurringOffers({ installments: Number(value) });
  };

  const handleOnChange = (id: string) => {
    handleSelectOffer(id, true);
  };

  return (
    <div className="PaymentOptions flex flex-col h-full">
      <div className="flex-1">
        {installments.length > 1 && canChooseInstallments ? (
          <div className="relative w-full self-start">
            <Autocomplete
              className="PaymentPlan-installments"
              name="installments"
              onChange={onInstallmentChange}
              options={installmentOptions}
              data-testid="installmentsAutocomplete"
              selected={selectedInstallment}
              required={true}
            >
              <Autocomplete.Input
                data-testid="installmentsDropdown"
                placeholder={i18n.t('paymentPlan.installments.label') as string}
              />
              <Autocomplete.Options />
            </Autocomplete>
          </div>
        ) : (
          <PlanSummary />
        )}
        {isFetchingRecurring ? (
          <Spinner className="PaymentPlan-spinner" />
        ) : (
          <div>
            <OptionList
              options={_options}
              errors={errors}
              dirtyFields={dirtyFields}
              isFetchingOffer={isFetchingOffer}
              onChange={handleOnChange}
              onFieldChange={handleFieldChange}
              values={values}
              type="recurring"
            />
            <div
              className="PaymentPlan-disclaimer"
              dangerouslySetInnerHTML={createMarkup(
                i18n.t('paymentPlan.disclaimer'),
              )}
            />
          </div>
        )}
      </div>
      <div className="pt-6 pb-4">
        <Terms.RecurringPayment
          checked={!!values.terms}
          onChange={handleTermsChange}
        />
        <Button
          onClick={handleOnClick}
          tone="primary"
          size="full"
          data-testid="next"
          disabled={!isValid || isSubmitting}
          className="mt-4"
        >
          {isSubmitting ? (
            <LoadingDots />
          ) : (
            i18n.t('checkout.paymentOptions.next')
          )}
        </Button>
      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  metadata: getRecurringOffersMetadata(state),
  options: getRecurringOffers(state),
  isFetchingRecurring: isFetching(state, 'recurringOffers'),
});

const mapDispatchToProps = {
  fetchRecurringOffers,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export const RecurringPaymentOptions = connector(RecurringPaymentComponent);
